import React, { useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import LazyLoad from "react-lazy-load"
import axios from "axios"

import {
  formatDate,
  getAppliedSortPressKit,
  sanitizeText,
  isUrlIncludeHttp,
  replaceSearchUrl,
  sortParameterURL,
} from "@/utils/helper"
import { getProducts } from "@/store/features/productListSlice"
import useWindowResize from "@/hooks/useWindowResize"
import { PRODUCTS_PER_PAGE, imgPlaceholderData } from "@/constants/index"

import Pagination from "@/components/ProductList/v2/ProductListView/Pagination/Pagination"
import LoadMore from "@/components/ProductList/v2/ProductListView/Pagination/LoadMore"
import Accordion from "@/components/core/Accordion/Accordion"
import Sort from "@/components/ProductList/v2/Sort"
import { scrollToProduct } from "@/components/Search/v1/ProductsTab/helper"
import { pressKitDataLayer } from "@/components/PressRoomLanding/v1/pressRoomAnalytics"

import styles from "@/components/PressRoomLanding/v1/PressKit.view/index.module.scss"
import downloadIcon from "@/public/icons/download-icon.svg"
import noImage from "@/public/images/image-missing.png"
import linkIcon from "@/public/icons/link-icon.svg"
import { getConfig } from "@/constants/config"

const PressRelease = ({ i18n = {}, refData = {} }) => {
  const [width] = useWindowResize()
  const [curRows, setCurRows] = useState(30)
  const [selectedSort, setSelectedSort] = useState({
    name: getAppliedSortPressKit(i18n, "pressReleaseDate_dt+desc"),
    query: "pressReleaseDate_dt+desc",
  })
  const [filterQueries, setFilterQueries] = useState([])
  const [start, setStart] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [formatedDate, setformatedDate] = useState([])
  const [dataPopulated, setDataPopulated] = useState(false)
  const [kitData, setKitData] = useState([])
  const [totalResults, setTotalResults] = useState(0)
  const dispatch = useDispatch()
  const [lwAppName, setLwAppName] = useState("")

  const isAcordianOpen = true
  const {
    sortByAsc = "",
    sortByDesc = "",
    sortByNewest = "",
    sortByOldest = "",
    viewPressKit = "",
    externalLink = "",
    accordianDetails = "",
    downloadPdf = "",
    ariaLabel = {},
  } = i18n
  const sorts = [
    { name: sortByAsc, query: "navTitle_s+asc" },
    { name: sortByDesc, query: "navTitle_s+desc" },
    { name: sortByNewest, query: "pressReleaseDate_dt+desc" },
    { name: sortByOldest, query: "pressReleaseDate_dt+asc" },
  ]
  useEffect(() => {
    const configFn = async () => {
      const { general } = await getConfig()
      setLwAppName(general?.lwAppName ?? "")
    }
    configFn()
  }, [])
  useEffect(() => {
    const formattedSort = sortParameterURL()

    if (formattedSort) {
      setSelectedSort({
        name: getAppliedSortPressKit(i18n, formattedSort),
        query: formattedSort,
      })
    }
  }, [])

  useEffect(() => {
    if (lwAppName) {
      if (start) {
        generateListingUrl(true, false)
      } else if (start == 0 && curRows) {
        generateListingUrl(false, false)
      } else {
        generateListingUrl()
      }
    }
  }, [start, curRows, selectedSort, lwAppName])
  useEffect(() => {
    // To scrollup the product list on back
    if (dataPopulated) scrollToProduct()
  }, [dataPopulated])

  useEffect(() => {
    const formatPressReleaseDates = kitData.map(pressKitInfo => {
      if (pressKitInfo && pressKitInfo["pressReleaseDate_dt"]) {
        return formatDate(pressKitInfo["pressReleaseDate_dt"])
          .then(formattedDate => ({
            ...pressKitInfo,
            formattedDate: formattedDate,
          }))
          .catch(() => pressKitInfo)
      }
      return Promise.resolve(pressKitInfo)
    })

    Promise.all(formatPressReleaseDates)
      .then(formattedData => setformatedDate(formattedData))
      .catch(err => console.log(err))
  }, [kitData])

  const onSort = selectedSort => {
    setStart(0)
    setSelectedSort(selectedSort)
    setIsOpen(false)
    replaceSearchUrl("sort", selectedSort.query)
  }

  const generateListingUrl = (update, enableScroll = true, queries) => {
    let callStart = 0
    if (queries) {
      setFilterQueries(queries)
    } else {
      queries = filterQueries
      callStart = start
    }
    const query = {
      rows: curRows,
      start: callStart,
    }
    if (selectedSort) query.sort = selectedSort.query
    const fl = [
      "id",
      "navTitle_s",
      "eyeBrowText_s",
      "page_description_content_s",
      "url_s",
      "jcr_created_s",
      "thumbNailImage_s",
      "pressReleaseDate_dt",
    ]
    const queryParams = {
      q: "*:*",
      fl: fl.join(","),
      ...query,
      displaytype: "search",
      update,
      fq: `templateName_s:("Press-Kit")`,
      profileName: `profile_${lwAppName?.toLowerCase()}_Search`,
      collections: lwAppName?.toLowerCase(),
    }
    if (enableScroll) setDataPopulated(false)
    dispatch(getProducts({ ...queryParams }))
      .unwrap()
      .then(res => {
        const {
          data: { data = [], total = "" },
        } = res
        if (update) {
          setKitData(prevData => [...prevData, ...data])
        } else {
          setKitData(data)
        }
        setTotalResults(total)
        if (enableScroll) setDataPopulated(true)
      })
      .catch(() => {
        if (enableScroll) setDataPopulated(false)
      })
  }

  const onRows = rows => {
    setStart(0)
    setCurRows(rows)
  }

  const onLoadMore = () => {
    setStart(start + curRows)
  }

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" })
    refData.current.focus()
  }

  const handleImgErr = e => (e.target.src = noImage?.src)

  const checkImg = (e, src) => {
    e.persist()
    const ele = e.target
    if (ele.src !== noImage?.src && ele.src === imgPlaceholderData) {
      axios
        .head(src)
        .then(res => {
          if (!res) handleImgErr(e)
        })
        .catch(() => handleImgErr(e))
    }
  }

  return (
    <div className={styles.myKitViewWrapper}>
      <div className="press-kit">
        <div className="row press-kit__sort-by-block m-0">
          <div className="col-lg-12 col-md-12 col-12 p-0">
            <Sort
              page="press-kits"
              data={sorts}
              texts={i18n}
              selectedSort={selectedSort}
              onChange={onSort}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
            />
          </div>
        </div>
        {kitData
          ? kitData?.map((pressKitInfo, index) => (
              <LazyLoad key={pressKitInfo.id} className="kf-lazyload-wrapper">
                <div className="press-kit__row">
                  <div className="col press-kit__info px-0">
                    <div className="press-kit__date">
                      {pressKitInfo && pressKitInfo["pressReleaseDate_dt"]
                        ? formatedDate[index]?.formattedDate ?? ""
                        : ""}
                    </div>
                    <h3 className="press-kit__title">
                      {pressKitInfo["navTitle_s"]
                        ? pressKitInfo["navTitle_s"]
                        : ""}
                    </h3>
                    <div className="press-kit__description">
                      {pressKitInfo ? pressKitInfo["eyeBrowText_s"] : ""}
                    </div>

                    {isAcordianOpen === true ? (
                      <div className="press-kit__view-kit-icon-block">
                        <a
                          className="gbh-data-layer"
                          target={
                            isUrlIncludeHttp(pressKitInfo["url_s"])
                              ? "_blank"
                              : ""
                          }
                          rel="noreferrer"
                          aria-label={ariaLabel?.viewPressKit(
                            pressKitInfo["navTitle_s"]
                              ? pressKitInfo["navTitle_s"]
                              : ""
                          )}
                          data-gbh-data-layer={pressKitDataLayer(pressKitInfo)}
                          href={pressKitInfo["url_s"] ?? "#"}
                        >
                          {isUrlIncludeHttp(pressKitInfo["url_s"]) && (
                            <img
                              className="press-kit__view-kit-icon"
                              src={linkIcon?.src}
                              alt={viewPressKit}
                            />
                          )}
                          <span className="press-kit__view-kit-link">
                            {isUrlIncludeHttp(pressKitInfo["url_s"])
                              ? externalLink
                              : viewPressKit}
                          </span>
                        </a>
                      </div>
                    ) : (
                      <div className="press-kit__view-accordian-icon-block">
                        <Accordion isOpen={false} title={viewPressKit}>
                          <div className="press-kit__view-accordian-kit-details">
                            <p className="press-kit__view-para">
                              {accordianDetails}
                            </p>
                            <div className="press-kit__download-block">
                              <div className="press-kit__download-icon-block float-left">
                                <img
                                  className="press-kit__download-icon"
                                  src={downloadIcon}
                                  alt={downloadPdf}
                                />
                              </div>
                              <div className="press-kit__download-link float-left">
                                {downloadPdf}
                              </div>
                            </div>
                          </div>
                          <div className="press-kit__view-accordian-kit-details">
                            <p className="press-kit__view-para">
                              {accordianDetails}
                            </p>
                            <div className="press-kit__download-block">
                              <div className="press-kit__download-icon-block float-left">
                                <img
                                  className="press-kit__download-icon"
                                  src={downloadIcon}
                                  alt={downloadPdf}
                                />
                              </div>
                              <div className="press-kit__download-link float-left">
                                {downloadPdf}
                              </div>
                            </div>
                          </div>
                          <div className="press-kit__view-accordian-kit-details">
                            <p className="press-kit__view-para">
                              {accordianDetails}
                            </p>
                          </div>
                        </Accordion>
                      </div>
                    )}
                  </div>

                  {pressKitInfo ? (
                    <div className="press-kit__image">
                      <img
                        src={pressKitInfo["thumbNailImage_s"]}
                        alt={
                          pressKitInfo["navTitle_s"]
                            ? sanitizeText(pressKitInfo["navTitle_s"])
                            : ""
                        }
                        className="cmp-image__image transition"
                        onLoad={e =>
                          checkImg(e, pressKitInfo["thumbNailImage_s"])
                        }
                        onError={e => handleImgErr(e)}
                      />
                    </div>
                  ) : null}
                </div>
              </LazyLoad>
            ))
          : null}

        {totalResults > curRows && totalResults > kitData.length ? (
          <div className="product-listing">
            <LoadMore
              displayShare
              displayPrint
              staticTexts={i18n}
              curRows={curRows}
              productsPerPage={PRODUCTS_PER_PAGE}
              totalResults={totalResults}
              onRows={onRows}
              handleBackToTop={handleBackToTop}
              page="press-kit"
              data={kitData}
              onLoadMore={onLoadMore}
              width={width}
            />
          </div>
        ) : null}
        {
          <div className="press-room__pagination">
            <Pagination
              displayShare
              displayPrint
              staticTexts={i18n}
              curRows={curRows}
              productsPerPage={PRODUCTS_PER_PAGE}
              totalResults={totalResults}
              onRows={onRows}
              handleBackToTop={handleBackToTop}
              page="press-kit"
              data={kitData}
              onLoadMore={onLoadMore}
              width={width}
              customClass={{
                buttonClass: "product-list__go-back",
                spanClass: "product-list__go-back-text",
                imgClass: "product-list__go-back-image",
              }}
              includeWrapper={false}
            />
          </div>
        }
      </div>
    </div>
  )
}

export default PressRelease
